/**
 * Hooks for the preference interview API (M1 Phase B). Reads mirror
 * ``useSystems``; the mutations (start / message / review / file) invalidate
 * the topic (and, for status changes, the topic list) so the UI reflects the
 * persisted conversation. An answer files nothing: stances reach the record
 * only through ``useFileStances``, with a decision on every proposed one.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi, type ApiError } from "@/lib/api";
import type {
  FinishResult,
  MessageResult,
  PreferenceGroup,
  StanceDecision,
  TopicDetail,
  TopicReview,
} from "@/lib/types";

const topicsKey = ["preference-topics"] as const;
const topicKey = (key: string) => ["preference-topic", key] as const;

export function useTopics() {
  const apiCall = useApi();
  return useQuery<PreferenceGroup[], ApiError>({
    queryKey: topicsKey,
    queryFn: ({ signal }) => apiCall<PreferenceGroup[]>("/api/preferences/topics", { signal }),
  });
}

export function useTopic(key: string) {
  const apiCall = useApi();
  return useQuery<TopicDetail, ApiError>({
    queryKey: topicKey(key),
    queryFn: ({ signal }) => apiCall<TopicDetail>(`/api/preferences/topics/${key}`, { signal }),
  });
}

export function useStartTopic(key: string) {
  const apiCall = useApi();
  const qc = useQueryClient();
  // Takes no argument: the opening turn is authored per topic on the backend
  // (`taxonomy/openers.py`), so there is no model call to shape for voice and
  // nothing for the caller to pass. Cheap enough that a cold start is one write.
  return useMutation<TopicDetail, ApiError, void>({
    mutationFn: () =>
      apiCall<TopicDetail>(`/api/preferences/topics/${key}/start`, {
        method: "POST",
      }),
    onSuccess: (detail) => {
      qc.setQueryData(topicKey(key), detail);
      void qc.invalidateQueries({ queryKey: topicsKey });
    },
  });
}

/** One outgoing answer. `voice` shapes the reply for being read aloud. */
export interface SendMessageInput {
  content: string;
  voice?: boolean;
}

export function useSendMessage(key: string) {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<MessageResult, ApiError, SendMessageInput>({
    mutationFn: ({ content, voice }) =>
      apiCall<MessageResult>(`/api/preferences/topics/${key}/messages`, {
        method: "POST",
        body: { content, voice: voice ?? false },
      }),
    // The reply is persisted server-side; refetch the topic for the full,
    // canonical transcript + accumulated signals. Return the promise so the
    // mutation stays pending until the refetch lands — callers can then clear
    // their optimistic bubble without a flicker/gap.
    onSuccess: () => {
      // An answer on a finished topic reopens it — the selection page's status
      // badge can change, so refresh the list too.
      void qc.invalidateQueries({ queryKey: topicsKey });
      return qc.invalidateQueries({ queryKey: topicKey(key) });
    },
  });
}

/** Draft what gets recorded from the conversation, for the person to approve. */
export function useReviewTopic(key: string) {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<TopicReview, ApiError, void>({
    mutationFn: () => apiCall<TopicReview>(`/api/preferences/topics/${key}/review`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: topicKey(key) }),
  });
}

/** File the approved stances and finish the topic. */
export function useFileStances(key: string) {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<FinishResult, ApiError, StanceDecision[]>({
    mutationFn: (decisions) =>
      apiCall<FinishResult>(`/api/preferences/topics/${key}/stances`, {
        method: "POST",
        body: { decisions },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: topicKey(key) });
      void qc.invalidateQueries({ queryKey: topicsKey });
    },
  });
}
